import React, { Suspense, useEffect } from 'react';
import './index.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import DebugOverlay from './components/DebugOverlay';
import { attachGlobalDebug, emit, isEnabled as isDebugEnabled } from './lib/debugBus';
import { isMediaModalOpen } from './lib/mediaModal';
import Login from './pages/Login';
import Home from './pages/Home';
import FAQ from './pages/FAQ';
import Contacts from './pages/Contacts';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AppShell from './layouts/AppShell';
import { Loader2 } from 'lucide-react';
import { useServiceWorkerUpdate } from './hooks/useServiceWorkerUpdate';

const Admin = React.lazy(() => import('./pages/Admin'));
const CareerTrackingPage = React.lazy(() => import('./pages/CareerTrackingPage'));
const CareerAnalysisPage = React.lazy(() => import('./pages/CareerAnalysisPage'));
const CalendarPage = React.lazy(() => import('./pages/CalendarPage'));
const SpillePage = React.lazy(() => import('./pages/SpillePage'));
const Business = React.lazy(() => import('./pages/Business'));
const BusinessContacts = React.lazy(() => import('./pages/BusinessContacts'));
const BusinessClients = React.lazy(() => import('./pages/BusinessClients'));
const BusinessTeam = React.lazy(() => import('./pages/BusinessTeam'));

function PageLoader() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center">
      <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin" />
    </div>
  );
}

function ServiceWorkerUpdater() {
  // Nessun popup: la nuova versione viene applicata in background (vedi hook),
  // così un deploy non costringe l'utente ad "Aggiorna" più volte al giorno.
  useServiceWorkerUpdate();
  return null;
}

function RouteTracer() {
  const location = useLocation();
  useEffect(() => {
    emit('route', { pathname: location.pathname, key: location.key });
  }, [location.pathname, location.key]);
  return null;
}

function AuthTracer() {
  const { user, loading, isAuthenticated } = useAuth();
  useEffect(() => {
    emit('auth', { loading, isAuthenticated, role: user?.role || null, status: user?.status || null });
  }, [user, loading, isAuthenticated]);
  return null;
}

const ProtectedRoute = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    if (isMediaModalOpen()) {
      return <Outlet />;
    }
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
};

const AdminRoute = () => {
  const { isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};

const AdminShell = () => <AppShell showBottomNav={false} />;

const AppRouter = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route element={<ProtectedRoute />}>
          <Route element={<AppShell />}>
            <Route path="/" element={<Home />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contatti" element={<Contacts />} />
            <Route path="/carriera" element={<CareerTrackingPage />} />
            <Route path="/carriera/grafici" element={<CareerAnalysisPage />} />
            <Route path="/calendario" element={<CalendarPage />} />
            <Route path="/spille" element={<SpillePage />} />
            <Route path="/business" element={<Business />} />
            <Route path="/business/contatti" element={<BusinessContacts />} />
            <Route path="/business/clienti" element={<BusinessClients />} />
            <Route path="/business/team" element={<BusinessTeam />} />
          </Route>
        </Route>

        <Route element={<AdminRoute />}>
          <Route element={<AdminShell />}>
            <Route path="/admin" element={<Admin />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  useEffect(() => {
    if (isDebugEnabled()) {
      attachGlobalDebug();
      // eslint-disable-next-line no-console
      console.info('[mtc-debug] overlay attivo. Riproduci il bug e premi "copia log".');
    }
  }, []);
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <ServiceWorkerUpdater />
          <RouteTracer />
          <AuthTracer />
          <AppRouter />
          {isDebugEnabled() ? <DebugOverlay /> : null}
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
